import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Calendar, MapPin, Users, Plane, Receipt, Building2, CreditCard, Loader2, Phone, Mail } from 'lucide-react';
import PortalItineraryView from '@/components/PortalItineraryView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [company, setCompany] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);
  const [payingStripe, setPayingStripe] = useState(false);

  useEffect(() => {
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/quote/${token}`);
      setQuote(response.data);
      setCompany(response.data.company);
      setItinerary(response.data.itinerary || null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Quote not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setResponding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/quote/${token}/accept`);
      toast.success('Quote accepted! Our team will contact you shortly.');
      setQuote(prev => ({ ...prev, status: 'accepted' }));
    } catch (error) {
      toast.error('Failed to accept quote');
    } finally {
      setResponding(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Please let us know the reason (optional):');
    setResponding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/quote/${token}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.info('Quote rejected');
      setQuote(prev => ({ ...prev, status: 'rejected' }));
    } catch (error) {
      toast.error('Failed to reject quote');
    } finally {
      setResponding(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  };

  const handlePayNow = async () => {
    setPayingStripe(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/checkout/stripe`, {
        tripId: quote.tripId,
        originUrl: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Failed to initiate payment');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initiation failed');
    } finally { setPayingStripe(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700 mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading your quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center bg-white rounded-3xl shadow-xl">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">Quote Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </Card>
      </div>
    );
  }

  const isResponded = quote.status === 'accepted' || quote.status === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header with Company Logo */}
        <div className="text-center mb-8">
          {company?.logoUrl && (
            <img 
              src={`${BACKEND_URL}${company.logoUrl}`} 
              alt={company.name}
              className="h-20 mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-heading font-bold text-slate-900">{company?.name || 'Travel Quote'}</h1>
          <p className="text-slate-600 mt-1">Quote #{quote.quoteNumber}</p>
          {quote.tripId && (
            <p className="text-xs text-slate-500 mt-1 tracking-wide" data-testid="client-portal-trip-id">
              Trip ID: <span className="font-semibold text-slate-700">{quote.displayTripId || quote.tripId}</span>
            </p>
          )}
        </div>

        {/* Status Banner */}
        {isResponded && (
          <Card className={`p-4 rounded-2xl ${quote.status === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center justify-center gap-3">
              {quote.status === 'accepted' ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-green-700 font-semibold">You have accepted this quote</span>
                </>
              ) : (
                <>
                  <XCircle className="h-6 w-6 text-red-600" />
                  <span className="text-red-700 font-semibold">You have rejected this quote</span>
                </>
              )}
            </div>
          </Card>
        )}

        {/* Quote Details */}
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6">Trip Details</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary-700 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Destination</p>
                <p className="font-semibold text-slate-900">{quote.destination}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Plane className="h-5 w-5 text-primary-700 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Departure From</p>
                <p className="font-semibold text-slate-900">{quote.departureCity || 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-primary-700 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Travel Dates</p>
                <p className="font-semibold text-slate-900">
                  {formatDate(quote.travelStartDate)} - {formatDate(quote.travelEndDate)}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary-700 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Travellers</p>
                <p className="font-semibold text-slate-900">
                  {quote.adults} Adults, {quote.children} Children, {quote.infants} Infants
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Pricing — client view shows ONLY the final price (breakdown hidden) */}
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="client-pricing-card">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary-700" />
            Total Package Price
          </h2>

          <div className="space-y-4">
            {quote.showFlightSeparately && (
              <div className="space-y-2" data-testid="client-flight-split">
                <div className="flex justify-between items-center py-2 px-4 rounded-lg bg-slate-50">
                  <span className="text-sm font-medium text-slate-600 flex items-center gap-2"><Plane className="h-4 w-4" /> Flights</span>
                  <span className="text-sm font-semibold text-slate-800" data-testid="client-flight-total">{formatCurrency(quote.flightTotal)}</span>
                </div>
                <div className="flex justify-between items-center py-2 px-4 rounded-lg bg-slate-50">
                  <span className="text-sm font-medium text-slate-600">Package</span>
                  <span className="text-sm font-semibold text-slate-800" data-testid="client-package-total">{formatCurrency(quote.packageTotal)}</span>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center py-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl px-6">
              <span className="text-lg font-bold text-primary-800">Final Price (All Inclusive)</span>
              <span className="text-2xl font-bold text-primary-700" data-testid="client-final-price">{formatCurrency(quote.totalCost)}</span>
            </div>
            <p className="text-xs text-slate-500 italic px-1">
              * Inclusive of GST{quote.locationType === 'international' && quote.tcsRate ? ' and applicable TCS' : ''}
            </p>

            {/* Pay Now Button */}
            {quote.paymentEnabled && quote.paymentStatus !== 'paid' && (
              <Button onClick={handlePayNow} disabled={payingStripe} data-testid="pay-now-btn"
                className="w-full h-14 mt-4 bg-gradient-to-br from-[#D4AF37] to-[#B8963A] text-white text-lg font-semibold rounded-xl hover:from-[#C9A531] hover:to-[#A8862F] shadow-lg">
                {payingStripe ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CreditCard className="h-5 w-5 mr-2" />}
                {payingStripe ? 'Redirecting...' : 'Pay Now'}
              </Button>
            )}
            {quote.paymentStatus === 'paid' && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center" data-testid="payment-confirmed-badge">
                <CheckCircle className="h-5 w-5 text-emerald-600 inline-block mr-2" />
                <span className="text-emerald-700 font-semibold">Payment Confirmed</span>
              </div>
            )}
          </div>
        </Card>

        {/* Linked Itinerary Details */}
        <PortalItineraryView itinerary={itinerary} />

        {/* Action Buttons */}
        {!isResponded && (
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleAccept}
              disabled={responding}
              data-testid="accept-quote-btn"
              className="flex-1 h-14 bg-gradient-to-br from-green-600 to-green-500 text-white text-lg font-semibold rounded-2xl hover:from-green-700 hover:to-green-600"
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Accept Quote
            </Button>
            
            <Button
              onClick={handleReject}
              disabled={responding}
              variant="outline"
              data-testid="reject-quote-btn"
              className="flex-1 h-14 border-2 border-slate-300 text-slate-700 text-lg font-semibold rounded-2xl hover:bg-slate-50"
            >
              <XCircle className="h-5 w-5 mr-2" />
              Reject Quote
            </Button>
          </div>
        )}

        {/* Contact */}
        <Card className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
          <div className="text-center">
            <p className="text-slate-600 mb-2">Questions about this quote?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {company?.phone && (
                <a href={`tel:${company.phone}`} className="text-primary-700 font-semibold hover:underline inline-flex items-center gap-1.5">
                  <Phone className="h-4 w-4" strokeWidth={1.75} /> {company.phone}
                </a>
              )}
              {company?.email && (
                <a href={`mailto:${company.email}`} className="text-primary-700 font-semibold hover:underline inline-flex items-center gap-1.5">
                  <Mail className="h-4 w-4" strokeWidth={1.75} /> {company.email}
                </a>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClientPortal;
